
// src/components/admin/page-management/section-forms/ContactFormSectionForm.js
import { useEffect, useState } from 'react';

const ContactFormSectionForm = ({ content, updateContent, section, pageId }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showFieldsConfig, setShowFieldsConfig] = useState(false);
  const [showLayoutOptions, setShowLayoutOptions] = useState(false);

  const [sectionId, setSectionId] = useState(null);

  // On component mount, try to get section ID if available
  useEffect(() => {
    if (section && section._id) {
      setSectionId(section._id);
    }
  }, [section]);


  // Helper to update nested contact info
  const updateContactInfo = (field, value) => {
    // Get existing contactInfo or create default structure
    const existingContactInfo = content?.contactInfo || {
      phone:  "+1 (888) 808-8869",
      email:"Info@PiTech.ca",
      address: "1814 Marine Dr, West Vancouver, BC, V7V 1J6",
      hours: {
        weekday: "Monday - Friday: 9 AM - 5 PM",
        weekend: "Saturday - Sunday: Closed"
      },
      socialMedia: {
        facebook: "",
        twitter: "",
        instagram: "",
        linkedin: ""
      }
    };
    
    // Create a deep copy to avoid modifying the original object
    const newContactInfo = JSON.parse(JSON.stringify(existingContactInfo));
    
    if (field.includes('.')) {
      // Handle nested field like 'hours.weekday' or 'socialMedia.facebook'
      const [parent, child] = field.split('.');
      
      // Ensure the parent object exists
      if (!newContactInfo[parent]) {
        newContactInfo[parent] = {};
      }
      
      newContactInfo[parent][child] = value;
    } else {
      // Handle top-level field
      newContactInfo[field] = value;
    }
    
    // Update the content with the modified contactInfo
    updateContent('contactInfo', newContactInfo);
  };
  

  const getFormFields = () => {
    // If content already has formFields and it's an array with entries, use those
    if (content?.formFields && Array.isArray(content.formFields) && content.formFields.length > 0) {
      return content.formFields;
    }
    
    // Otherwise return default fields
    return defaultFormFields;
  };
  
  // Helper to update form fields
  const updateFormField = (index, property, value) => {
    // Get existing formFields or use default
    const formFields = Array.isArray(content?.formFields) ? 
      [...content.formFields] : 
      [...defaultFormFields];
    
    // Update the field
    formFields[index] = {
      ...formFields[index],
      [property]: value
    };
    
    // Simply use the existing updateContent function with non-stringified array
    updateContent('formFields', formFields);
  };

// Add new form field
const addFormField = () => {
  // Get existing formFields - ensure we're always working with a copy
  const formFields = Array.isArray(content?.formFields) ? 
    [...content.formFields] : 
    [...defaultFormFields];
  
  // Add a new field with unique ID
  const newField = {
    id: `field_${Date.now()}`,
    type: 'text',
    label: 'New Field',
    placeholder: 'Enter value',
    required: false,
    width: 'full'
  };
  
  // Add to array
  formFields.push(newField);
  
  console.log('Adding new field, updated formFields:', formFields);
  
  // Update the content with the new fields array
  updateContent('formFields', formFields);
};

// Remove form field
const removeFormField = (index) => {
  // Get existing formFields or use default
  const formFields = Array.isArray(content?.formFields) ? 
    [...content.formFields] : 
    [...defaultFormFields];
  
  // Remove the field at the specified index
  const updatedFields = formFields.filter((_, i) => i !== index);
  
  // Update the content with the modified fields
  updateContent('formFields', updatedFields);
};

// Move form field up/down
const moveFormField = (index, direction) => {
  // Get existing formFields or use default
  const formFields = Array.isArray(content?.formFields) ? 
    [...content.formFields] : 
    [...defaultFormFields];
  
  // Create a copy of the array
  const updatedFields = [...formFields];
  
  // Move field up or down
  if (direction === 'up' && index > 0) {
    [updatedFields[index], updatedFields[index - 1]] = 
    [updatedFields[index - 1], updatedFields[index]];
  } else if (direction === 'down' && index < updatedFields.length - 1) {
    [updatedFields[index], updatedFields[index + 1]] = 
    [updatedFields[index + 1], updatedFields[index]];
  }
  
  // Update the content with the reordered fields
  updateContent('formFields', updatedFields);
};

 
  // Default form fields if not defined
  const defaultFormFields = [
    {
      id: 'name',
      type: 'text',
      label: 'Your Name',
      placeholder: 'Your Name',
      required: true,
      width: 'half'
    },
    {
      id: 'email',
      type: 'email',
      label: 'Your Email',
      placeholder: 'Your Email',
      required: true,
      width: 'half'
    },
    {
      id: 'phone',
      type: 'tel',
      label: 'Phone Number',
      placeholder: 'Phone Number (Optional)',
      required: false,
      width: 'half'
    },
    {
      id: 'company',
      type: 'text',
      label: 'Company Name',
      placeholder: 'Company Name (Optional)',
      required: false,
      width: 'half'
    },
    {
      id: 'message',
      type: 'textarea',
      label: 'Your Message',
      placeholder: 'Your Message',
      required: true,
      width: 'full'
    }
  ];

const InfoTooltip = ({ children, tooltip }) => (
  <div className="relative group inline-block">
    {children}
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
      {tooltip}
      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
    </div>
  </div>
);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Contact Form Configuration</h3>



<div className="space-y-4">
  {/* Form Section Title */}
  {/* Contact Panel Title Configuration */}
<div className="space-y-3">
  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
    <label className="block font-medium text-gray-700">
      Contact Panel Title  
    </label>
    <input
      type="text"
      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white hover:border-gray-400 text-lg"
      value={content?.contactPanelTitle || ''}
      onChange={(e) => updateContent('contactPanelTitle', e.target.value)}
      placeholder="Contact Us"
    />
    <p className="text-xs text-gray-500 mt-1">This will be displayed as the main heading in the contact information panel</p>
  </div>
</div>

{/* Info Section Additional Text */}
<div className="space-y-3">
  <div className="border border-gray-200 rounded-lg p-4 bg-indigo-50">
    <label className="block font-medium text-gray-700 mb-2">
      Info Section Additional Text
    </label>
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1">
          Main Text
        </label>
        <textarea
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white hover:border-gray-400 resize-none"
          value={content?.infoSectionText || ''}
          onChange={(e) => updateContent('infoSectionText', e.target.value)}
          placeholder="Add additional information about your business..."
          rows="3"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1">
          Description Text
        </label>
        <textarea
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white hover:border-gray-400 resize-none"
          value={content?.infoSectionDescription || ''}
          onChange={(e) => updateContent('infoSectionDescription', e.target.value)}
          placeholder="Add a description or call-to-action..."
          rows="2"
        />
      </div>
    </div>
    <p className="text-xs text-gray-500 mt-2">This text will appear in the contact information panel below the title</p>
  </div>
</div>

  <div className="items-center justify-between">
  <label className="block font-medium text-gray-700">
      Form Title  
  </label>
    <input
      type="text"
      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white hover:border-gray-400 text-lg"
      value={content?.title || ''}
      onChange={(e) => updateContent('title', e.target.value)}
      placeholder="Send Us a Message"
    />
    </div>

  {/* Form Submit Button Text */}
 <div className="space-y-3"> 
   <div className="items-center justify-between border border-gray-200 rounded-lg p-4 bg-gray-50">

  <label className="block font-medium text-gray-700">
      Submit Button Text
  </label>
      <input
        type="text"
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white hover:border-gray-400 font-medium"
        value={content?.submitButtonText || ''}
        onChange={(e) => updateContent('submitButtonText', e.target.value)}
        placeholder="Send Message"
      />
      <div className="flex flex-wrap gap-2">
        <span className="text-xs text-gray-500">Popular options:</span>
        {['Send Message', 'Contact Us', 'Get In Touch', 'Submit', 'Send Now'].map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => updateContent('submitButtonText', option)}
            className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors duration-200"
          >
            {option}
          </button>
        ))}
      </div>

      
    </div>
</div>
  {/* Success Message */}
 
    <div className="space-y-3">
      <div className="items-center justify-between border border-gray-200 rounded-lg p-4 bg-green-50">
      <label className="block font-medium text-gray-700">
        Success Message
      </label>
      <textarea
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white hover:border-gray-400 resize-none"
        value={content?.successMessage || ''}
        onChange={(e) => updateContent('successMessage', e.target.value)}
        placeholder="Thank you for your message! We'll get back to you soon."
        rows="3"
      />
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>Keep it friendly and reassuring</span>
        <span>{(content?.successMessage || '').length}/200</span>
      </div>
      <div className="flex flex-wrap gap-2">
        <span className="text-xs text-gray-500">Templates:</span>
        {[
          "Thank you! We'll respond within 24 hours.",
          "Message received! Our team will contact you shortly.",
          "Thanks for reaching out! We'll get back to you soon."
        ].map((template, index) => (
          <button
            key={index}
            type="button"
            onClick={() => updateContent('successMessage', template)}
            className="px-3 py-1 text-xs bg-green-50 hover:bg-green-100 text-green-700 rounded-full transition-colors duration-200"
          >
            Template {index + 1}
          </button>
        ))}
      </div>
    </div>
</div>
  {/* Error Message */}
  
    <div className="space-y-3">
      <div className="items-center justify-between border border-gray-200 rounded-lg p-4 bg-red-50">
      <label className="block font-medium text-gray-700"> 
        Error Message
      </label>
      <textarea
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200 bg-white hover:border-gray-400 resize-none"
        value={content?.errorMessage || ''}
        onChange={(e) => updateContent('errorMessage', e.target.value)}
        placeholder="Sorry, there was an error sending your message. Please try again."
        rows="3"
      />
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>Be helpful and suggest what to do next</span>
        <span>{(content?.errorMessage || '').length}/200</span>
      </div>
      <div className="flex flex-wrap gap-2">
        <span className="text-xs text-gray-500">Templates:</span>
        {[
          "Oops! Please try again or contact us directly.",
          "Something went wrong. Please refresh and try again.",
          "Error occurred. Please contact us at [email] instead."
        ].map((template, index) => (
          <button
            key={index}
            type="button"
            onClick={() => updateContent('errorMessage', template)}
            className="px-3 py-1 text-xs bg-red-50 hover:bg-red-100 text-red-700 rounded-full transition-colors duration-200"
          >
            Template {index + 1}
          </button>
        ))}
      </div>
    </div>
</div>
  {/* Show Contact Information Panel */}
  
    <div className="space-y-4">
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center space-x-3">
          <div className={`w-4 h-4 rounded-full transition-colors duration-200 ${content?.showContactInfo !== false ? 'bg-green-500' : 'bg-gray-300'}`}></div>
          <div>
            <p className="font-medium text-gray-800">Contact Information Panel</p>
            <p className="text-sm text-gray-600">
              {content?.showContactInfo !== false 
                ? 'Your contact details will be displayed next to the form' 
                : 'Only the contact form will be shown'
              }
            </p>
          </div>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={content?.showContactInfo !== false}
            onChange={(e) => updateContent('showContactInfo', e.target.checked)}
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>
      
{content?.showContactInfo !== false && (
  <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-100 border border-blue-200 rounded-xl p-6 shadow-sm">
    {/* Header with animation */}
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center">
        <div className="flex items-center justify-center w-10 h-10 bg-blue-600 rounded-full shadow-md mr-3">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div>
          <h4 className="font-semibold text-blue-900">Contact Panel Active</h4>
          <p className="text-sm text-blue-700">Information panel is enabled</p>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
        <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">Live</span>
      </div>
    </div>

    {/* Description */}
    <div className="mb-4 p-3 bg-white/60 rounded-lg border border-blue-100">
      <p className="text-sm text-blue-800 leading-relaxed">
        Your business information will be beautifully displayed alongside the contact form, creating a professional and comprehensive contact experience for your visitors.
      </p>
    </div>

    {/* Feature Grid */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
      {[
        { 
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          ),
          title: 'Contact Details',
          desc: 'Phone, email & address'
        },
        { 
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          title: 'Business Hours',
          desc: 'Weekday & weekend hours'
        },
        { 
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-10 0a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2" />
            </svg>
          ),
          title: 'Social Media',
          desc: 'Connected platforms'
        },
        { 
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          ),
          title: 'Location Info',
          desc: 'Address & directions'
        }
      ].map((feature, index) => (
        <div key={index} className="flex items-center p-3 bg-white/70 rounded-lg border border-blue-100 hover:bg-white/90 transition-colors duration-200">
          <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-lg mr-3 text-blue-600">
            {feature.icon}
          </div>
          <div>
            <h5 className="font-medium text-blue-900 text-sm">{feature.title}</h5>
            <p className="text-xs text-blue-600">{feature.desc}</p>
          </div>
        </div>
      ))}
    </div>

    {/* Quick Actions */}
    <div className="flex items-center justify-between pt-3 border-t border-blue-200">
      <div className="flex items-center text-xs text-blue-600">
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Configure contact details below
      </div>
      
    </div>

    {/* Decorative gradient overlay */}
    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-400/20 to-transparent rounded-full -mr-10 -mt-10"></div>
  </div>
)}


      
      {content?.showContactInfo === false && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-center mb-2">
            <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <span className="font-medium text-gray-800">Form Only Mode</span>
          </div>
          <p className="text-sm text-gray-600">
            Only the contact form will be displayed. Contact information will be hidden.
          </p>
        </div>
      )}
    </div>
</div>
         
{content?.showContactInfo !== false && (
  <div className="mt-8" data-section="contact-info">
    {/* Section Header */}
    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 mb-6 text-white shadow-lg">
      <div className="flex items-center justify-between">
          
         <div>
          <h3 className="text-xl font-bold mb-2">
          <svg className="w-12 h-12 text-white inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>Contact Information Setup</h3>
          <p className="text-blue-100">Configure the business details that will appear alongside your contact form</p>
        </div>
        
      </div>
    </div>

    {/* Business Details Section */}
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
      <div className="flex items-center mb-4">
        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <h4 className="text-lg font-semibold text-gray-800">Business Details</h4>
      </div>

      <div className="grid md:grid-cols-2 gap-6"> 
        <div className="space-y-2">
          <label className="flex items-center text-sm font-medium text-gray-700">
            <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            Phone Number
          </label>
          <input
            type="tel"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200  hover:border-gray-400"
            value={content?.contactInfo?.phone || ''}
            onChange={(e) => updateContactInfo('phone', e.target.value)}
            placeholder="+1 (888) 808-8869"
          />
          <p className="text-xs text-gray-500">Include country code for international visitors</p>
        </div>

        {/* Email Address */}
        <div className="space-y-2">
          <label className="flex items-center text-sm font-medium text-gray-700">
            <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Email Address
          </label>
          <input
            type="email"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white hover:border-gray-400"
            value={content?.contactInfo?.email || ''}
            onChange={(e) => updateContactInfo('email', e.target.value)}
            placeholder="info@PiTech.com"
          />
          <p className="text-xs text-gray-500">Primary contact email for inquiries</p>
        </div>

        {/* Business Address */}
        <div className="md:col-span-2 space-y-2">
          <label className="flex items-center text-sm font-medium text-gray-700">
            <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Business Address
          </label>
          <input
            type="text"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white hover:border-gray-400"
            value={content?.contactInfo?.address || ''}
            onChange={(e) => updateContactInfo('address', e.target.value)}
            placeholder="1814 Marine Dr, West Vancouver, BC, V7V 1J6"
          />
          <p className="text-xs text-gray-500">Full address including city, state/province, and postal code</p>
        </div>
      </div>
    </div>

    {/* Business Hours Section */}
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
      <div className="flex items-center mb-4">
        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h4 className="text-lg font-semibold text-gray-800">Business Hours</h4>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Weekday Hours */}
        <div className="space-y-2">
          <label className="flex items-center text-sm font-medium text-gray-700">
            <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Weekday Hours
          </label>
          <input
            type="text"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white hover:border-gray-400"
            value={content?.contactInfo?.hours?.weekday || ''}
            onChange={(e) => updateContactInfo('hours.weekday', e.target.value)}
            placeholder="Monday - Friday: 9 AM - 5 PM"
          />
          <div className="flex flex-wrap gap-2 mt-2">
            <span className="text-xs text-gray-500">Quick options:</span>
            {[
              'Monday - Friday: 9 AM - 5 PM',
              'Mon-Fri: 8:00 AM - 6:00 PM',
              'Weekdays: 9:00 AM - 5:30 PM'
            ].map((option, index) => (
              <button
                key={index}
                type="button"
                onClick={() => updateContactInfo('hours.weekday', option)}
                className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors duration-200"
              >
                Option {index + 1}
              </button>
            ))}
          </div>
        </div>

        {/* Weekend Hours */}
        <div className="space-y-2">
          <label className="flex items-center text-sm font-medium text-gray-700">
            <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
            Weekend Hours
          </label>
          <input
            type="text"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white hover:border-gray-400"
            value={content?.contactInfo?.hours?.weekend || ''}
            onChange={(e) => updateContactInfo('hours.weekend', e.target.value)}
            placeholder="Saturday - Sunday: Closed"
          />
          <div className="flex flex-wrap gap-2 mt-2">
            <span className="text-xs text-gray-500">Quick options:</span>
            {[
              'Saturday - Sunday: Closed',
              'Weekends: 10 AM - 4 PM',
              'Sat: 9 AM - 2 PM, Sun: Closed'
            ].map((option, index) => (
              <button
                key={index}
                type="button"
                onClick={() => updateContactInfo('hours.weekend', option)}
                className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors duration-200"
              >
                Option {index + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* Social Media Section */}
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-10 0a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2" />
            </svg>
          </div>
          <div>
            <h4 className="text-lg font-semibold text-gray-800">Social Media Links</h4>
            <p className="text-sm text-gray-600">Optional - Connect your social media profiles</p>
          </div>
        </div>
        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">Optional</span>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Facebook */}
        <div className="space-y-2">
          <label className="flex items-center text-sm font-medium text-gray-700">
            <svg className="w-4 h-4 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
            Facebook Page
          </label>
          <input
            type="url"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white hover:border-gray-400"
            value={content?.contactInfo?.socialMedia?.facebook || ''}
            onChange={(e) => updateContactInfo('socialMedia.facebook', e.target.value)}
            placeholder="https://facebook.com/yourcompany"
          />
        </div>

        {/* Twitter */}
        <div className="space-y-2">
          <label className="flex items-center text-sm font-medium text-gray-700">
            <svg className="w-4 h-4 mr-2 text-sky-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
            </svg>
            Twitter Profile
          </label>
          <input
            type="url"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white hover:border-gray-400"
            value={content?.contactInfo?.socialMedia?.twitter || ''}
            onChange={(e) => updateContactInfo('socialMedia.twitter', e.target.value)}
            placeholder="https://twitter.com/yourcompany"
          />
        </div>

        {/* Instagram */}
        <div className="space-y-2">
          <label className="flex items-center text-sm font-medium text-gray-700">
            <svg className="w-4 h-4 mr-2 text-pink-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.624 5.367 11.99 11.988 11.99s11.99-5.366 11.99-11.99C24.007 5.367 18.641.001 12.017.001zm5.568 16.614c-.895 0-1.837-.197-2.312-.615-.463-.41-.615-.895-.615-2.312V9.313c0-1.417.152-1.902.615-2.312.475-.418 1.417-.615 2.312-.615h-11.15c.895 0 1.837.197 2.312.615.463.41.615.895.615 2.312v4.374c0 1.417-.152 1.902-.615 2.312-.475.418-1.417.615-2.312.615h11.15z" />
            </svg>
            Instagram Profile
          </label>
          <input
            type="url"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white hover:border-gray-400"
            value={content?.contactInfo?.socialMedia?.instagram || ''}
            onChange={(e) => updateContactInfo('socialMedia.instagram', e.target.value)}
            placeholder="https://instagram.com/yourcompany"
          />
        </div>

        {/* LinkedIn */}
        <div className="space-y-2">
          <label className="flex items-center text-sm font-medium text-gray-700">
            <svg className="w-4 h-4 mr-2 text-blue-700" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
            LinkedIn Company Page
          </label>
          <input
            type="url"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white hover:border-gray-400"
            value={content?.contactInfo?.socialMedia?.linkedin || ''}
            onChange={(e) => updateContactInfo('socialMedia.linkedin', e.target.value)}
            placeholder="https://linkedin.com/company/yourcompany"
          />
        </div>
      </div>

      {/* Social Media Help */}
      <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm">
            <p className="font-medium text-purple-800 mb-1">Social Media Tips:</p>
            <ul className="text-purple-700 space-y-1 text-xs">
              <li>• Only filled URLs will display as clickable social icons</li>
              <li>• Use complete URLs starting with https://</li>
              <li>• Icons will appear in the order: Facebook, Twitter, Instagram, LinkedIn</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
)}


{/* Enhanced Form Fields Configuration */}
<div className="mt-8">
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
    {/* Section Header */}
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center">
        <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center mr-4">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">Form Fields Builder</h3>
          <p className="text-sm text-gray-600">Customize the fields in your contact form</p>
        </div>
      </div>
      <button
        type="button"
        className="inline-flex items-center px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-medium rounded-lg transition-colors duration-200 border border-indigo-200"
        onClick={() => setShowFieldsConfig(!showFieldsConfig)}
      >
        <svg className={`w-4 h-4 mr-2 transition-transform duration-200 ${showFieldsConfig ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
        {showFieldsConfig ? 'Hide Form Builder' : 'Show Form Builder'}
      </button>
    </div>

    {/* Collapsed State Preview */}
    {!showFieldsConfig && (
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex items-center space-x-2 mr-4">
              <span className="inline-flex items-center px-2 py-1 bg-indigo-100 text-indigo-800 text-xs font-medium rounded-md">
                {getFormFields().length} Fields
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-md">
                {getFormFields().filter(f => f.required).length} Required
              </span>
            </div>
            <div className="text-sm text-gray-600">
              Current fields: {getFormFields().map(f => f.label).join(', ')}
            </div>
          </div>
          
        </div>
      </div>
    )}

    {/* Expanded Configuration */}
    {showFieldsConfig && (
      <div className="space-y-6">
        {/* Instructions */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="font-medium text-blue-900 mb-1">How to Use the Form Builder</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Drag fields up/down to reorder them</li>
                <li>• Use different field types for different data (email, phone, text)</li>
                <li>• Set field width to control layout (full, half, or third width)</li>
                <li>• Mark important fields as required</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Form Fields List */}
        <div className="space-y-4">
          {getFormFields().length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No form fields yet</h3>
              <p className="text-gray-600 mb-4">Start building your contact form by adding your first field</p>
              <button
                type="button"
                onClick={addFormField}
                className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors duration-200"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Your First Field
              </button>
            </div>
          ) : (
            <>
              {getFormFields().map((field, index) => (
                <div key={field.id} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
                  {/* Field Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-600 rounded-lg mr-3 font-semibold text-sm">
                        {index + 1}
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900">{field.label || `Field ${index + 1}`}</h5>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                            field.type === 'email' ? 'bg-blue-100 text-blue-800' :
                            field.type === 'tel' ? 'bg-green-100 text-green-800' :
                            field.type === 'textarea' ? 'bg-purple-100 text-purple-800' :
                            field.type === 'select' ? 'bg-orange-100 text-orange-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {field.type}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                            field.width === 'full' ? 'bg-blue-100 text-blue-800' :
                            field.width === 'half' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {field.width} width
                          </span>
                          {field.required && (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">
                              Required
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Field Actions */}
                    <div className="flex items-center space-x-2">
                      <button 
                        type="button"
                        onClick={() => moveFormField(index, 'up')}
                        disabled={index === 0}
                        className={`p-2 rounded-lg transition-colors duration-200 ${
                          index === 0 
                            ? 'text-gray-300 cursor-not-allowed' 
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                        }`}
                        title="Move up"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button 
                        type="button"
                        onClick={() => moveFormField(index, 'down')}
                        disabled={index === getFormFields().length - 1}
                        className={`p-2 rounded-lg transition-colors duration-200 ${
                          index === getFormFields().length - 1
                            ? 'text-gray-300 cursor-not-allowed' 
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                        }`}
                        title="Move down"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      <button 
                        type="button"
                        onClick={() => removeFormField(index)}
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        title="Remove field"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  {/* Field Configuration */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Field Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Field Type
                      </label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                        value={field.type}
                        onChange={(e) => updateFormField(index, 'type', e.target.value)}
                      >
                       <option value="text">Text Input</option>
                        <option value="email">Email Address</option>
                        <option value="tel">Phone Number</option>
                        <option value="number">Number</option>
                        <option value="url">URL</option>
                        <option value="password">Password</option>
                        <option value="date">Date</option>
                        <option value="time">Time</option>
                        <option value="textarea">Multi-line Text</option>
                        <option value="select">Dropdown Menu</option>
                        <option value="radio">Radio Group</option>
                        <option value="checkbox">Checkbox</option>
                        <option value="file">File Upload</option>
                      </select>
                    </div>
                    
                    {/* Field Width */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Field Width
                      </label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                        value={field.width}
                        onChange={(e) => updateFormField(index, 'width', e.target.value)}
                      >
                        <option value="full">Full Width (100%)</option>
                        <option value="half">Half Width (50%)</option>
                        <option value="third">One Third (33%)</option>
                      </select>
                    </div>
                    
                    {/* Field Label */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Field Label
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                        value={field.label || ''}
                        onChange={(e) => updateFormField(index, 'label', e.target.value)}
                        placeholder="Enter field label"
                      />
                    </div>
                    
                    {/* Field Placeholder */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Placeholder Text
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                        value={field.placeholder || ''}
                        onChange={(e) => updateFormField(index, 'placeholder', e.target.value)}
                        placeholder="Enter placeholder text"
                      />
                    </div>
                    
                    {/* Dropdown Options */}
                    {field.type === 'select' && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Dropdown Options
                        </label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                          value={field.options || ''}
                          onChange={(e) => updateFormField(index, 'options', e.target.value)}
                          placeholder="Option 1, Option 2, Option 3"
                        />
                        <p className="text-xs text-gray-500 mt-1">Separate options with commas</p>
                      </div>
                    )}
                    
                    {/* Required Field Toggle */}
                    <div className="md:col-span-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 transition-colors duration-200"
                          checked={field.required || false}
                          onChange={(e) => updateFormField(index, 'required', e.target.checked)}
                        />
                        <span className="ml-3 text-sm font-medium text-gray-700">
                          This field is required
                        </span>
                      </label>
                    </div>

                    
{/* Additional Field Options for Radio, Select, and File */}
{(field.type === 'select' || field.type === 'radio') && (
  <div className="md:col-span-2">
    <label className="block text-sm font-medium text-gray-700 mb-2">
      Options
    </label>
    <input
      type="text"
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
      value={field.options || ''}
      onChange={(e) => updateFormField(index, 'options', e.target.value)}
      placeholder="Option 1, Option 2, Option 3"
    />
    <p className="text-xs text-gray-500 mt-1">Separate options with commas</p>
  </div>
)}

{field.type === 'radio' && (
  <div className="md:col-span-2">
    <label className="flex items-center cursor-pointer">
      <input
        type="checkbox"
        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 transition-colors duration-200"
        checked={field.radioInline || false}
        onChange={(e) => updateFormField(index, 'radioInline', e.target.checked)}
      />
      <span className="ml-3 text-sm font-medium text-gray-700">
        Display options horizontally (inline)
      </span>
    </label>
  </div>
)}

{field.type === 'file' && (
  <>
    <div className="md:col-span-1">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Accepted File Types
      </label>
      <input
        type="text"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
        value={field.accept || ''}
        onChange={(e) => updateFormField(index, 'accept', e.target.value)}
        placeholder=".pdf,.doc,.docx"
      />
    </div>
    <div className="md:col-span-1">
      <label className="flex items-center cursor-pointer mt-6">
        <input
          type="checkbox"
          className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 transition-colors duration-200"
          checked={field.multiple || false}
          onChange={(e) => updateFormField(index, 'multiple', e.target.checked)}
        />
        <span className="ml-3 text-sm font-medium text-gray-700">
          Allow multiple files
        </span>
      </label>
    </div>
    <div className="md:col-span-2">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Help Text
      </label>
      <input
        type="text"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
        value={field.helpText || ''}
        onChange={(e) => updateFormField(index, 'helpText', e.target.value)}
        placeholder="Maximum file size: 5MB"
      />
    </div>
  </>
)}

{field.type === 'number' && (
  <>
    <div className="md:col-span-1">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Min Value
      </label>
      <input
        type="number"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
        value={field.min || ''}
        onChange={(e) => updateFormField(index, 'min', e.target.value)}
        placeholder="0"
      />
    </div>
    <div className="md:col-span-1">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Max Value
      </label>
      <input
        type="number"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
        value={field.max || ''}
        onChange={(e) => updateFormField(index, 'max', e.target.value)}
        placeholder="100"
      />
    </div>
  </>
)}

{(field.type === 'date') && (
  <>
    <div className="md:col-span-1">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Min Date
      </label>
      <input
        type="date"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
        value={field.min || ''}
        onChange={(e) => updateFormField(index, 'min', e.target.value)}
      />
    </div>
    <div className="md:col-span-1">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Max Date
      </label>
      <input
        type="date"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
        value={field.max || ''}
        onChange={(e) => updateFormField(index, 'max', e.target.value)}
      />
    </div>
  </>
)}
                  </div>
                </div>
              ))}

              {/* Add Field Button */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={addFormField}
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add New Field
                </button>
              </div>
            </>
          )}
        </div>

        {/* Field Statistics */}
        {getFormFields().length > 0 && (
          <div className="bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">Form Statistics</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-indigo-600">{getFormFields().length}</div>
                <div className="text-xs text-gray-600">Total Fields</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{getFormFields().filter(f => f.required).length}</div>
                <div className="text-xs text-gray-600">Required Fields</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{getFormFields().filter(f => f.width === 'full').length}</div>
                <div className="text-xs text-gray-600">Full Width</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{getFormFields().filter(f => f.width === 'half').length}</div>
                <div className="text-xs text-gray-600">Half Width</div>
              </div>
            </div>
          </div>
        )}
      </div>
    )}
  </div>
</div>

{/* SMS Notification Section */}
<div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
  <div className="flex items-center mb-4">
    <svg className="w-6 h-6 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
    <h4 className="text-lg font-semibold text-green-900">SMS Notifications</h4>
  </div>

  <div className="space-y-4">
    {/* Enable SMS Notifications */}
    <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-green-200">
      <div className="flex items-center">
        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </div>
        <div>
          <h5 className="font-medium text-gray-900">SMS Admin Notifications</h5>
          <p className="text-sm text-gray-600">Send SMS alerts to administrators when forms are submitted</p>
        </div>
      </div>
      <label className="relative inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          className="sr-only peer"
          checked={content?.enableSmsNotifications === true}
          onChange={(e) => updateContent('enableSmsNotifications', e.target.checked)}
        />
        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
      </label>
    </div>

    {/* SMS Configuration */}
    {content?.enableSmsNotifications && (
      <div className="space-y-4 p-4 bg-white rounded-lg border border-green-200">
        {/* Admin Phone Numbers */}
        <div>
          <label className="block text-sm font-medium text-green-800 mb-2">
            Admin Phone Numbers
          </label>
          <textarea
            className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white resize-none"
            value={content?.adminPhoneNumbers || ''}
            onChange={(e) => updateContent('adminPhoneNumbers', e.target.value)}
            placeholder="+1234567890, +0987654321"
            rows="3"
          />
          <p className="text-xs text-green-600 mt-1">
            Enter phone numbers separated by commas. Include country codes (e.g., +1 for US/Canada)
          </p>
        </div>

        {/* SMS Message Template */}
        <div>
          <label className="block text-sm font-medium text-green-800 mb-2">
            SMS Message Template
          </label>
          <textarea
            className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 bg-white resize-none"
            value={content?.smsTemplate || ''}
            onChange={(e) => updateContent('smsTemplate', e.target.value)}
            placeholder="New contact form submission from {name} ({email}). Message: {message}. Please checkout your email inbox to see the full form details"
            rows="4"
          />
          <p className="text-xs text-green-600 mt-1">
            Use {`{fieldName}`} to include form field values. Available: {`{name}, {email}, {phone}, {company}, {message}`}
          </p>
        </div>

        {/* Character Count Warning */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="text-sm">
              <p className="font-medium text-amber-800 mb-1">SMS Length Notice</p>
              <p className="text-amber-700">
                SMS messages over 160 characters will be split into multiple messages. Keep your template concise for best results.
              </p>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* SMS Notice */}
    {content?.enableSmsNotifications && (
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm">
            <p className="font-medium text-blue-800 mb-1">SMS Configuration Required</p>
            <p className="text-blue-700">
              SMS notifications require Twilio configuration in your server environment variables. 
              Contact your developer to set up TWILIO_PHONE_NUMBER, TWILIO_ACCOUNT_SID, and TWILIO_AUTH_TOKEN.
            </p>
          </div>
        </div>
      </div>
    )}
  </div>
</div>

      
      {/* Enhanced Layout Options */}
<div className="mt-8">
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
    {/* Section Header */}
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center">
        <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-600 rounded-lg flex items-center justify-center mr-4">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
          </svg>
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">Layout & Design</h3>
          <p className="text-sm text-gray-600">Customize the appearance and layout of your contact form</p>
        </div>
      </div>
      <button
        type="button"
        className="inline-flex items-center px-4 py-2 bg-green-50 hover:bg-green-100 text-green-700 font-medium rounded-lg transition-colors duration-200 border border-green-200"
        onClick={() => setShowLayoutOptions(!showLayoutOptions)}
      >
        <svg className={`w-4 h-4 mr-2 transition-transform duration-200 ${showLayoutOptions ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
        {showLayoutOptions ? 'Hide Layout Options' : 'Show Layout Options'}
      </button>
    </div>




    {/* Collapsed State Preview */}
    {!showLayoutOptions && (
      <div className="bg-gradient-to-r from-green-50 to-teal-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-md">
                {content?.layoutType || 'side-by-side'}
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-md">
                {content?.borderRadius || 'rounded-xl'}
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-md">
                {content?.shadowStyle || 'shadow-xl'}
              </span>
            </div>
            <div className="text-sm text-gray-600">
              Current layout settings configured
            </div>
          </div>
         
        </div>
      </div>
    )}

    {/* Expanded Configuration */}
    {showLayoutOptions && (
      <div className="space-y-6">
        {/* Layout Type Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <svg className="w-6 h-6 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
            </svg>
            <h4 className="text-lg font-semibold text-blue-900">Layout Structure</h4>
          </div>
          
          <div className="space-y-3">
            <label className="block text-sm font-medium text-blue-800 mb-2">
              Choose how to arrange your form and contact information
            </label>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { value: 'side-by-side', label: 'Side by Side', desc: 'Info panel left, form right' },
                { value: 'form-top', label: 'Form Above Info', desc: 'Form on top, info below'},
                { value: 'info-top', label: 'Info Above Form', desc: 'Info on top, form below' },
                { value: 'form-only', label: 'Form Only', desc: 'Hide contact info panel'}
              ].map((layout) => (
                <label key={layout.value} className="cursor-pointer">
                  <input
                    type="radio"
                    name="layoutType"
                    value={layout.value}
                    checked={content?.layoutType === layout.value || (!content?.layoutType && layout.value === 'side-by-side')}
                    onChange={(e) => updateContent('layoutType', e.target.value)}
                    className="sr-only"
                  />
                  <div className={`p-4 border-2 rounded-lg transition-all duration-200 ${
                    content?.layoutType === layout.value || (!content?.layoutType && layout.value === 'side-by-side')
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-300 bg-white hover:border-blue-300'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">{layout.label}</span>
                      <span className="text-lg">{layout.icon}</span>
                    </div>
                    <p className="text-sm text-gray-600">{layout.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Visual Style Section */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <svg className="w-6 h-6 text-purple-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
            </svg>
            <h4 className="text-lg font-semibold text-purple-900">Visual Styling</h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Border Radius */}
            <div>
              <label className="block text-sm font-medium text-purple-800 mb-2">
                Corner Rounding
              </label>
              <select
                className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 bg-white"
                value={content?.borderRadius || 'rounded-xl'}
                onChange={(e) => updateContent('borderRadius', e.target.value)}
              >
                <option value="rounded-none">Sharp Corners</option>
                <option value="rounded">Slightly Rounded</option>
                <option value="rounded-md">Medium Rounded</option>
                <option value="rounded-lg">Well Rounded</option>
                <option value="rounded-xl">Very Rounded</option>
                <option value="rounded-full">Pill Shape</option>
              </select>
              <div className="mt-2 flex items-center justify-center h-8 bg-white border border-purple-200 rounded-lg">
                <div className={`w-16 h-4 bg-purple-200 ${content?.borderRadius || 'rounded-xl'}`}></div>
              </div>
            </div>

            {/* Shadow Style */}
            <div>
              <label className="block text-sm font-medium text-purple-800 mb-2">
                Shadow Effect
              </label>
              <select
                className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 bg-white"
                value={content?.shadowStyle || 'shadow-xl'}
                onChange={(e) => updateContent('shadowStyle', e.target.value)}
              >
                <option value="shadow-none">No Shadow</option>
                <option value="shadow-sm">Subtle</option>
                <option value="shadow">Light</option>
                <option value="shadow-md">Medium</option>
                <option value="shadow-lg">Strong</option>
                <option value="shadow-xl">Very Strong</option>
              </select>
              <div className="mt-2 flex items-center justify-center h-8 bg-gray-100 rounded-lg">
                <div className={`w-16 h-4 bg-white rounded ${content?.shadowStyle || 'shadow-xl'}`}></div>
              </div>
            </div>

            {/* Padding */}
            <div>
              <label className="block text-sm font-medium text-purple-800 mb-2">
                Internal Spacing
              </label>
              <select
                className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 bg-white"
                value={content?.padding || 'p-12'}
                onChange={(e) => updateContent('padding', e.target.value)}
              >
                <option value="p-4">Compact (16px)</option>
                <option value="p-6">Cozy (24px)</option>
                <option value="p-8">Comfortable (32px)</option>
                <option value="p-12">Spacious (48px)</option>
                <option value="p-16">Luxurious (64px)</option>
              </select>
              <div className="mt-2 flex items-center justify-center h-8 bg-white  rounded-lg border border-purple-200">
                <div className={`bg-purple-100 rounded ${content?.padding || 'p-12'} scale-50`}>
                  <div className="w-8 h-2 bg-purple-300 rounded"></div>
                </div>
                </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <svg className="w-6 h-6 text-orange-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
            </svg>
            <h4 className="text-lg font-semibold text-orange-900">Additional Features</h4>
          </div>

          <div className="space-y-4">
            {/* Social Media Icons */}
            <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-orange-200">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                  <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-10 0a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2" />
                  </svg>
                </div>
                <div>
                  <h5 className="font-medium text-gray-900">Social Media Icons</h5>
                  <p className="text-sm text-gray-600">Display clickable social media icons in the contact panel</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={content?.showSocialIcons !== false}
                  onChange={(e) => updateContent('showSocialIcons', e.target.checked)}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
              </label>
            </div>

            {/* CAPTCHA Protection */}
            <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-orange-200">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h5 className="font-medium text-gray-900">CAPTCHA Protection</h5>
                  <p className="text-sm text-gray-600">Add spam protection to your contact form</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={content?.enableCaptcha === true}
                  onChange={(e) => updateContent('enableCaptcha', e.target.checked)}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
              </label>
            </div>

            {/* CAPTCHA Notice */}
            {content?.enableCaptcha && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm">
                    <p className="font-medium text-red-800 mb-1">CAPTCHA Configuration Required</p>
                    <p className="text-red-700">You'll need to configure CAPTCHA settings in your server configuration to enable spam protection.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        
      </div>
    )}
  </div>
</div>
  {/* Enhanced Advanced Settings */}
<div className="mt-8">
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
    {/* Section Header */}
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center">
        <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center mr-4">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
          </svg>
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">Advanced Styling</h3>
          <p className="text-sm text-gray-600">Fine-tune colors and advanced customizations</p>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <span className="inline-flex items-center px-2 py-1 bg-violet-100 text-violet-800 text-xs font-medium rounded-md">
          Pro Features
        </span>
        <button
          type="button"
          className="inline-flex items-center px-4 py-2 bg-violet-50 hover:bg-violet-100 text-violet-700 font-medium rounded-lg transition-colors duration-200 border border-violet-200"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          <svg className={`w-4 h-4 mr-2 transition-transform duration-200 ${showAdvanced ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
          {showAdvanced ? 'Hide Advanced Options' : 'Show Advanced Options'}
        </button>
      </div>
    </div>

    {/* Collapsed State Preview */}
    {!showAdvanced && (
      <div className="bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: content?.backgroundColor || '#1A4B84' }}></div>
              <span className="text-xs text-gray-600">Panel Color</span>
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: content?.buttonColor || '#1A4B84' }}></div>
              <span className="text-xs text-gray-600">Button Color</span>
              <div className="w-4 h-4 rounded-full border" style={{ borderColor: content?.fieldBorderColor || '#e5e7eb' }}></div>
              <span className="text-xs text-gray-600">Field Border</span>
            </div>
            <div className="text-sm text-gray-600">
              Advanced styling configured
            </div>
          </div>
          <svg className="w-5 h-5 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
          </svg>
        </div>
      </div>
    )}

    {/* Expanded Configuration */}
    {showAdvanced && (
      <div className="space-y-6">
        {/* Warning Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.88-.833-2.5 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <h4 className="font-medium text-amber-800 mb-1">Advanced User Settings</h4>
              <p className="text-sm text-amber-700">These options provide detailed control over your form's appearance. Changes will affect the visual design of your contact form.</p>
            </div>
          </div>
        </div>

        {/* Color Customization Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <svg className="w-6 h-6 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
            </svg>
            <h4 className="text-lg font-semibold text-blue-900">Color Scheme</h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Info Panel Colors */}
            <div className="space-y-4">
              <h5 className="font-medium text-blue-800 border-b border-blue-200 pb-2">Contact Panel Colors</h5>
              
              {/* Panel Background */}
              <div>
                <label className="block text-sm font-medium text-blue-800 mb-2">
                  Background Color
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    className="h-12 w-12 p-1 border border-blue-300 rounded-lg cursor-pointer"
                    value={content?.backgroundColor || '#1A4B84'}
                    onChange={(e) => updateContent('backgroundColor', e.target.value)}
                  />
                  <input
                    type="text"
                    className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 font-mono text-sm"
                    value={content?.backgroundColor || '#1A4B84'}
                    onChange={(e) => updateContent('backgroundColor', e.target.value)}
                    placeholder="#1A4B84"
                  />
                </div>
                <div className="mt-2 h-8 rounded-lg border" style={{ backgroundColor: content?.backgroundColor || '#1A4B84' }}></div>
              </div>

              {/* Panel Text Color */}
              <div>
                <label className="block text-sm font-medium text-blue-800 mb-2">
                  Text Color
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    className="h-12 w-12 p-1 border border-blue-300 rounded-lg cursor-pointer"
                    value={content?.textColor || '#ffffff'}
                    onChange={(e) => updateContent('textColor', e.target.value)}
                  />
                  <input
                    type="text"
                    className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 font-mono text-sm"
                    value={content?.textColor || '#ffffff'}
                    onChange={(e) => updateContent('textColor', e.target.value)}
                    placeholder="#ffffff"
                  />
                </div>
                <div className="mt-2 h-8 rounded-lg border flex items-center px-3 text-sm font-medium" 
                     style={{ 
                       backgroundColor: content?.backgroundColor || '#1A4B84',
                       color: content?.textColor || '#ffffff'
                     }}>
                  Sample Text
                </div>
              </div>
            </div>

            {/* Form Colors */}
            <div className="space-y-4">
              <h5 className="font-medium text-blue-800 border-b border-blue-200 pb-2">Form Colors</h5>
              
              {/* Form Background */}
              <div>
                <label className="block text-sm font-medium text-blue-800 mb-2">
                  Form Background
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    className="h-12 w-12 p-1 border border-blue-300 rounded-lg cursor-pointer"
                    value={content?.formBackgroundColor || '#ffffff'}
                    onChange={(e) => updateContent('formBackgroundColor', e.target.value)}
                  />
                  <input
                    type="text"
                    className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 font-mono text-sm"
                    value={content?.formBackgroundColor || '#ffffff'}
                    onChange={(e) => updateContent('formBackgroundColor', e.target.value)}
                    placeholder="#ffffff"
                  />
                </div>
                <div className="mt-2 h-8 rounded-lg border" style={{ backgroundColor: content?.formBackgroundColor || '#ffffff' }}></div>
              </div>

              {/* Field Border Color */}
              <div>
                <label className="block text-sm font-medium text-blue-800 mb-2">
                  Field Border Color
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    className="h-12 w-12 p-1 border border-blue-300 rounded-lg cursor-pointer"
                    value={content?.fieldBorderColor || '#e5e7eb'}
                    onChange={(e) => updateContent('fieldBorderColor', e.target.value)}
                  />
                  <input
                    type="text"
                    className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 font-mono text-sm"
                    value={content?.fieldBorderColor || '#e5e7eb'}
                    onChange={(e) => updateContent('fieldBorderColor', e.target.value)}
                    placeholder="#e5e7eb"
                  />
                </div>
                <div className="mt-2 h-8 rounded-lg border-2 bg-white" style={{ borderColor: content?.fieldBorderColor || '#e5e7eb' }}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Button Customization Section */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <svg className="w-6 h-6 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
            </svg>
            <h4 className="text-lg font-semibold text-green-900">Button Styling</h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Button Background Color */}
            <div>
              <label className="block text-sm font-medium text-green-800 mb-2">
                Button Background Color
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  className="h-12 w-12 p-1 border border-green-300 rounded-lg cursor-pointer"
                  value={content?.buttonColor || '#1A4B84'}
                  onChange={(e) => updateContent('buttonColor', e.target.value)}
                />
                <input
                  type="text"
                  className="flex-1 px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 font-mono text-sm"
                  value={content?.buttonColor || '#1A4B84'}
                  onChange={(e) => updateContent('buttonColor', e.target.value)}
                  placeholder="#1A4B84"
                />
              </div>
            </div>

            {/* Button Text Color */}
            <div>
              <label className="block text-sm font-medium text-green-800 mb-2">
                Button Text Color
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  className="h-12 w-12 p-1 border border-green-300 rounded-lg cursor-pointer"
                  value={content?.buttonTextColor || '#ffffff'}
                  onChange={(e) => updateContent('buttonTextColor', e.target.value)}
                />
                <input
                  type="text"
                  className="flex-1 px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 font-mono text-sm"
                  value={content?.buttonTextColor || '#ffffff'}
                  onChange={(e) => updateContent('buttonTextColor', e.target.value)}
                  placeholder="#ffffff"
                />
              </div>
            </div>
          </div>

          {/* Button Preview */}
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              className="px-6 py-3 rounded-lg font-medium transition-colors duration-200 cursor-default"
              style={{ 
                backgroundColor: content?.buttonColor || '#1A4B84',
                color: content?.buttonTextColor || '#ffffff'
              }}
            >
              {content?.submitButtonText || 'Send Message'}
            </button>
          </div>
        </div>

        {/* Form Field Styling */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <svg className="w-6 h-6 text-purple-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h4 className="text-lg font-semibold text-purple-900">Form Field Options</h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Field Border Radius */}
            <div>
              <label className="block text-sm font-medium text-purple-800 mb-2">
                Field Corner Rounding
              </label>
              <select
                className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 bg-white"
                value={content?.fieldBorderRadius || 'rounded-lg'}
                onChange={(e) => updateContent('fieldBorderRadius', e.target.value)}
              >
                <option value="rounded-none">Sharp Corners</option>
                <option value="rounded">Slightly Rounded</option>
                <option value="rounded-md">Medium Rounded</option>
                <option value="rounded-lg">Well Rounded</option>
                <option value="rounded-xl">Very Rounded</option>
              </select>
              <div className="mt-2 flex justify-center">
                <div className={`w-24 h-8 border-2 bg-white ${content?.fieldBorderRadius || 'rounded-lg'}`} 
                     style={{ borderColor: content?.fieldBorderColor || '#e5e7eb' }}></div>
              </div>
            </div>

            {/* Button Width Option */}
            <div>
              <label className="block text-sm font-medium text-purple-800 mb-2">
                Button Width Setting
              </label>
              <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-purple-200">
                <div>
                  <h5 className="font-medium text-gray-900">Full Width Submit Button</h5>
                  <p className="text-sm text-gray-600">Button spans the entire form width</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={content?.buttonFullWidth !== false}
                    onChange={(e) => updateContent('buttonFullWidth', e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
            </div>
          </div>
        </div>
        

        {/* Custom CSS Section */}
        <div className="bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <svg className="w-6 h-6 text-gray-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
            <div>
              <h4 className="text-lg font-semibold text-gray-900">Custom CSS</h4>
              <p className="text-sm text-gray-600">For developers: Add custom styling code</p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-200">
                  Custom CSS Code
                </label>
                <div className="flex items-center space-x-2">
                  <span className="inline-flex items-center px-2 py-1 bg-gray-700 text-gray-300 text-xs font-medium rounded">
                    CSS
                  </span>
                  <span className="inline-flex items-center px-2 py-1 bg-red-900 text-red-200 text-xs font-medium rounded">
                    Advanced
                  </span>
                </div>
              </div>
              <textarea
                className="w-full p-3 bg-gray-900 text-gray-100 border border-gray-600 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                value={content?.customCss || ''}
                onChange={(e) => updateContent('customCss', e.target.value)}
                placeholder={`.contact-form {
                  /* Add your custom styles here */
                  border: 1px solid #e5e7eb;
                  background: #ffffff;
                }

                .contact-form .form-field {
                  /* Style individual form fields */
                }`}
                rows="8"
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-sm">
                  <p className="font-medium text-blue-800 mb-1">CSS Tips:</p>
                  <ul className="text-blue-700 space-y-1 text-xs list-disc list-inside">
                    <li>Use .contact-form as the main container selector</li>
                    <li>Target .form-field for individual input styling</li>
                    <li>Add !important for overriding existing styles</li>
                    <li>Test changes carefully before publishing</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

  
      </div>
    )}
  </div>
</div>
      
      
      <div className="bg-blue-50 p-4 rounded-md mb-6">
        <h4 className="text-blue-800 font-medium mb-2">Important Notes</h4>
        <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
          <li>Form validation will automatically handle required fields</li>
          <li>All form submissions will be sent to your specified email</li>
          <li>Social media links will only appear if URLs are provided</li>
          {content?.enableCaptcha && (
            <li>CAPTCHA requires additional server-side configuration</li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default ContactFormSectionForm;



// // before Saba's edition
// // src/components/admin/page-management/section-forms/ContactFormSectionForm.js
// import React, { useState,useEffect } from 'react';

// const ContactFormSectionForm = ({ content, updateContent, section, pageId }) => {
//   const [showAdvanced, setShowAdvanced] = useState(false);
//   const [showFieldsConfig, setShowFieldsConfig] = useState(false);
//   const [showLayoutOptions, setShowLayoutOptions] = useState(false);

//   const [sectionId, setSectionId] = useState(null);

//   // On component mount, try to get section ID if available
//   useEffect(() => {
//     if (section && section._id) {
//       setSectionId(section._id);
//     }
//   }, [section]);


//   // Helper to update nested contact info
//   const updateContactInfo = (field, value) => {
//     // Get existing contactInfo or create default structure
//     const existingContactInfo = content?.contactInfo || {
//       phone: process.env.REACT_APP_COMPANY_PHONE_NUMBER || "+1 (833) 333-3101",
//       email: process.env.REACT_APP_COMPANY_EMAIL || "Info@bagbox.ca",
//       address: process.env.REACT_APP_COMPANY_ADDRESS || "102-1127 14th St W, North Vancouver, BC, V7P 1J9",
//       hours: {
//         weekday: "Monday - Friday: 9 AM - 5 PM",
//         weekend: "Saturday - Sunday: Closed"
//       },
//       socialMedia: {
//         facebook: "",
//         twitter: "",
//         instagram: "",
//         linkedin: ""
//       }
//     };
    
//     // Create a deep copy to avoid modifying the original object
//     const newContactInfo = JSON.parse(JSON.stringify(existingContactInfo));
    
//     if (field.includes('.')) {
//       // Handle nested field like 'hours.weekday' or 'socialMedia.facebook'
//       const [parent, child] = field.split('.');
      
//       // Ensure the parent object exists
//       if (!newContactInfo[parent]) {
//         newContactInfo[parent] = {};
//       }
      
//       newContactInfo[parent][child] = value;
//     } else {
//       // Handle top-level field
//       newContactInfo[field] = value;
//     }
    
//     // Update the content with the modified contactInfo
//     updateContent('contactInfo', newContactInfo);
//   };
  

//   const getFormFields = () => {
//     // If content already has formFields and it's an array with entries, use those
//     if (content?.formFields && Array.isArray(content.formFields) && content.formFields.length > 0) {
//       return content.formFields;
//     }
    
//     // Otherwise return default fields
//     return defaultFormFields;
//   };
  
//   // Helper to update form fields
//   const updateFormField = (index, property, value) => {
//     // Get existing formFields or use default
//     const formFields = Array.isArray(content?.formFields) ? 
//       [...content.formFields] : 
//       [...defaultFormFields];
    
//     // Update the field
//     formFields[index] = {
//       ...formFields[index],
//       [property]: value
//     };
    
//     // Simply use the existing updateContent function with non-stringified array
//     updateContent('formFields', formFields);
//   };

// // Add new form field
// const addFormField = () => {
//   // Get existing formFields - ensure we're always working with a copy
//   const formFields = Array.isArray(content?.formFields) ? 
//     [...content.formFields] : 
//     [...defaultFormFields];
  
//   // Add a new field with unique ID
//   const newField = {
//     id: `field_${Date.now()}`,
//     type: 'text',
//     label: 'New Field',
//     placeholder: 'Enter value',
//     required: false,
//     width: 'full'
//   };
  
//   // Add to array
//   formFields.push(newField);
  
//   console.log('Adding new field, updated formFields:', formFields);
  
//   // Update the content with the new fields array
//   updateContent('formFields', formFields);
// };

// // Remove form field
// const removeFormField = (index) => {
//   // Get existing formFields or use default
//   const formFields = Array.isArray(content?.formFields) ? 
//     [...content.formFields] : 
//     [...defaultFormFields];
  
//   // Remove the field at the specified index
//   const updatedFields = formFields.filter((_, i) => i !== index);
  
//   // Update the content with the modified fields
//   updateContent('formFields', updatedFields);
// };

// // Move form field up/down
// const moveFormField = (index, direction) => {
//   // Get existing formFields or use default
//   const formFields = Array.isArray(content?.formFields) ? 
//     [...content.formFields] : 
//     [...defaultFormFields];
  
//   // Create a copy of the array
//   const updatedFields = [...formFields];
  
//   // Move field up or down
//   if (direction === 'up' && index > 0) {
//     [updatedFields[index], updatedFields[index - 1]] = 
//     [updatedFields[index - 1], updatedFields[index]];
//   } else if (direction === 'down' && index < updatedFields.length - 1) {
//     [updatedFields[index], updatedFields[index + 1]] = 
//     [updatedFields[index + 1], updatedFields[index]];
//   }
  
//   // Update the content with the reordered fields
//   updateContent('formFields', updatedFields);
// };

 
//   // Default form fields if not defined
//   const defaultFormFields = [
//     {
//       id: 'name',
//       type: 'text',
//       label: 'Your Name',
//       placeholder: 'Your Name',
//       required: true,
//       width: 'half'
//     },
//     {
//       id: 'email',
//       type: 'email',
//       label: 'Your Email',
//       placeholder: 'Your Email',
//       required: true,
//       width: 'half'
//     },
//     {
//       id: 'phone',
//       type: 'tel',
//       label: 'Phone Number',
//       placeholder: 'Phone Number (Optional)',
//       required: false,
//       width: 'half'
//     },
//     {
//       id: 'company',
//       type: 'text',
//       label: 'Company Name',
//       placeholder: 'Company Name (Optional)',
//       required: false,
//       width: 'half'
//     },
//     {
//       id: 'message',
//       type: 'textarea',
//       label: 'Your Message',
//       placeholder: 'Your Message',
//       required: true,
//       width: 'full'
//     }
//   ];

//   return (
//     <div className="space-y-4">
//       <h3 className="text-lg font-medium">Contact Form Configuration</h3>
      
//       <div className="mb-4">
//         <label className="block text-sm font-medium text-gray-700 mb-1">
//           Form Section Title
//         </label>
//         <input
//           type="text"
//           className="w-full p-2 border rounded"
//           value={content?.title || 'Send Us a Message'}
//           onChange={(e) => updateContent('title', e.target.value)}
//           placeholder="Send Us a Message"
//         />
//       </div>
      
//       <div className="mb-4">
//         <label className="block text-sm font-medium text-gray-700 mb-1">
//           Form Submit Button Text
//         </label>
//         <input
//           type="text"
//           className="w-full p-2 border rounded"
//           value={content?.submitButtonText || 'Send Message'}
//           onChange={(e) => updateContent('submitButtonText', e.target.value)}
//           placeholder="Send Message"
//         />
//       </div>
      
//       <div className="mb-4">
//         <label className="block text-sm font-medium text-gray-700 mb-1">
//           Success Message
//         </label>
//         <textarea
//           className="w-full p-2 border rounded"
//           value={content?.successMessage || "Thank you for your message! We'll get back to you soon."}
//           onChange={(e) => updateContent('successMessage', e.target.value)}
//           placeholder="Thank you for your message! We'll get back to you soon."
//           rows="2"
//         />
//       </div>
      
//       <div className="mb-4">
//         <label className="block text-sm font-medium text-gray-700 mb-1">
//           Error Message
//         </label>
//         <textarea
//           className="w-full p-2 border rounded"
//           value={content?.errorMessage || "Sorry, there was an error sending your message. Please try again."}
//           onChange={(e) => updateContent('errorMessage', e.target.value)}
//           placeholder="Sorry, there was an error sending your message. Please try again."
//           rows="2"
//         />
//       </div>
      
//       <div className="mb-4">
//         <label className="flex items-center text-sm font-medium text-gray-700">
//           <input
//             type="checkbox"
//             className="mr-2 h-4 w-4"
//             checked={content?.showContactInfo !== false}
//             onChange={(e) => updateContent('showContactInfo', e.target.checked)}
//           />
//           Show Contact Information Panel
//         </label>
//       </div>
      
//       {content?.showContactInfo !== false && (
//         <div className="border-t border-gray-200 pt-4 mb-4">
//           <h4 className="font-medium text-gray-800 mb-3">Contact Information</h4>
          
//           <div className="mb-4">
//             <label className="block text-sm font-medium text-gray-700 mb-1">
//               Phone Number
//             </label>
//             <input
//               type="text"
//               className="w-full p-2 border rounded"
//               value={content?.contactInfo?.phone || process.env.REACT_APP_COMPANY_PHONE_NUMBER || '+1 (833) 333-3101'}
//               onChange={(e) => updateContactInfo('phone', e.target.value)}
//               placeholder="+1 (833) 333-3101"
//             />
//           </div>
          
//           <div className="mb-4">
//             <label className="block text-sm font-medium text-gray-700 mb-1">
//               Email Address
//             </label>
//             <input
//               type="text"
//               className="w-full p-2 border rounded"
//               value={content?.contactInfo?.email || process.env.REACT_APP_COMPANY_EMAIL || 'Info@bagbox.ca'}
//               onChange={(e) => updateContactInfo('email', e.target.value)}
//               placeholder="Info@bagbox.ca"
//             />
//           </div>
          
//           <div className="mb-4">
//             <label className="block text-sm font-medium text-gray-700 mb-1">
//               Business Address
//             </label>
//             <input
//               type="text"
//               className="w-full p-2 border rounded"
//               value={content?.contactInfo?.address || process.env.REACT_APP_COMPANY_ADDRESS || '102-1127 14th St W, North Vancouver, BC, V7P 1J9'}
//               onChange={(e) => updateContactInfo('address', e.target.value)}
//               placeholder="102-1127 14th St W, North Vancouver, BC, V7P 1J9"
//             />
//           </div>
          
//           <div className="mb-4">
//             <label className="block text-sm font-medium text-gray-700 mb-1">
//               Weekday Hours
//             </label>
//             <input
//               type="text"
//               className="w-full p-2 border rounded"
//               value={content?.contactInfo?.hours?.weekday || 'Monday - Friday: 9 AM - 5 PM'}
//               onChange={(e) => updateContactInfo('hours.weekday', e.target.value)}
//               placeholder="Monday - Friday: 9 AM - 5 PM"
//             />
//           </div>
          
//           <div className="mb-4">
//             <label className="block text-sm font-medium text-gray-700 mb-1">
//               Weekend Hours
//             </label>
//             <input
//               type="text"
//               className="w-full p-2 border rounded"
//               value={content?.contactInfo?.hours?.weekend || 'Saturday - Sunday: Closed'}
//               onChange={(e) => updateContactInfo('hours.weekend', e.target.value)}
//               placeholder="Saturday - Sunday: Closed"
//             />
//           </div>
          
//           <h4 className="font-medium text-gray-800 mb-3 mt-4">Social Media (Optional)</h4>
          
//           <div className="mb-4">
//             <label className="block text-sm font-medium text-gray-700 mb-1">
//               Facebook URL
//             </label>
//             <input
//               type="text"
//               className="w-full p-2 border rounded"
//               value={content?.contactInfo?.socialMedia?.facebook || ''}
//               onChange={(e) => updateContactInfo('socialMedia.facebook', e.target.value)}
//               placeholder="https://facebook.com/yourcompany"
//             />
//           </div>
          
//           <div className="mb-4">
//             <label className="block text-sm font-medium text-gray-700 mb-1">
//               Twitter URL
//             </label>
//             <input
//               type="text"
//               className="w-full p-2 border rounded"
//               value={content?.contactInfo?.socialMedia?.twitter || ''}
//               onChange={(e) => updateContactInfo('socialMedia.twitter', e.target.value)}
//               placeholder="https://twitter.com/yourcompany"
//             />
//           </div>
          
//           <div className="mb-4">
//             <label className="block text-sm font-medium text-gray-700 mb-1">
//               Instagram URL
//             </label>
//             <input
//               type="text"
//               className="w-full p-2 border rounded"
//               value={content?.contactInfo?.socialMedia?.instagram || ''}
//               onChange={(e) => updateContactInfo('socialMedia.instagram', e.target.value)}
//               placeholder="https://instagram.com/yourcompany"
//             />
//           </div>
          
//           <div className="mb-4">
//             <label className="block text-sm font-medium text-gray-700 mb-1">
//               LinkedIn URL
//             </label>
//             <input
//               type="text"
//               className="w-full p-2 border rounded"
//               value={content?.contactInfo?.socialMedia?.linkedin || ''}
//               onChange={(e) => updateContactInfo('socialMedia.linkedin', e.target.value)}
//               placeholder="https://linkedin.com/company/yourcompany"
//             />
//           </div>
//         </div>
//       )}
      
//       {/* Form Fields Configuration */}
//       <div className="border-t border-gray-200 pt-4">
//         <button
//           type="button"
//           className="text-sm text-blue-600 hover:text-blue-800 mb-4"
//           onClick={() => setShowFieldsConfig(!showFieldsConfig)}
//         >
//           {showFieldsConfig ? 'Hide Form Fields Configuration' : 'Show Form Fields Configuration'}
//         </button>
        
//         {showFieldsConfig && (
//           <div className="space-y-4">
//             <h4 className="font-medium text-gray-800 mb-3">Form Fields</h4>
            
//             <div className="bg-gray-50 p-4 rounded">
//               <p className="text-sm text-gray-700 mb-2">Configure the fields that appear in your contact form:</p>
              
//               {getFormFields().map((field, index) => (
//                 <div key={field.id} className="border rounded-lg p-4 mb-4 bg-white">
//                   <div className="flex justify-between items-center mb-3">
//                     <h5 className="font-medium">{field.label || `Field ${index + 1}`}</h5>
//                     <div className="flex space-x-2">
//                       <button 
//                         type="button"
//                         onClick={() => moveFormField(index, 'up')}
//                         disabled={index === 0}
//                         className={`text-gray-500 ${index === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-700'}`}
//                       >
//                         ↑
//                       </button>
//                       <button 
//                         type="button"
//                         onClick={() => moveFormField(index, 'down')}
//                         disabled={index === (content?.formFields || defaultFormFields).length - 1}
//                         className={`text-gray-500 ${index === (content?.formFields || defaultFormFields).length - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-700'}`}
//                       >
//                         ↓
//                       </button>
//                       <button 
//                         type="button"
//                         onClick={() => removeFormField(index)}
//                         className="text-red-500 hover:text-red-700"
//                       >
//                         ×
//                       </button>
//                     </div>
//                   </div>
                  
//                   <div className="grid grid-cols-2 gap-3">
//                     <div>
//                       <label className="block text-xs font-medium text-gray-700 mb-1">
//                         Field Type
//                       </label>
//                       <select
//                         className="w-full p-2 border rounded text-sm"
//                         value={field.type}
//                         onChange={(e) => updateFormField(index, 'type', e.target.value)}
//                       >
//                         <option value="text">Text</option>
//                         <option value="email">Email</option>
//                         <option value="tel">Phone</option>
//                         <option value="textarea">Text Area</option>
//                         <option value="select">Dropdown</option>
//                         <option value="checkbox">Checkbox</option>
//                       </select>
//                     </div>
                    
//                     <div>
//                       <label className="block text-xs font-medium text-gray-700 mb-1">
//                         Field Width
//                       </label>
//                       <select
//                         className="w-full p-2 border rounded text-sm"
//                         value={field.width}
//                         onChange={(e) => updateFormField(index, 'width', e.target.value)}
//                       >
//                         <option value="full">Full Width</option>
//                         <option value="half">Half Width</option>
//                         <option value="third">One Third</option>
//                       </select>
//                     </div>
                    
//                     <div className="col-span-2">
//                       <label className="block text-xs font-medium text-gray-700 mb-1">
//                         Label
//                       </label>
//                       <input
//                         type="text"
//                         className="w-full p-2 border rounded text-sm"
//                         value={field.label}
//                         onChange={(e) => updateFormField(index, 'label', e.target.value)}
//                         placeholder="Field Label"
//                       />
//                     </div>
                    
//                     <div className="col-span-2">
//                       <label className="block text-xs font-medium text-gray-700 mb-1">
//                         Placeholder
//                       </label>
//                       <input
//                         type="text"
//                         className="w-full p-2 border rounded text-sm"
//                         value={field.placeholder}
//                         onChange={(e) => updateFormField(index, 'placeholder', e.target.value)}
//                         placeholder="Field Placeholder"
//                       />
//                     </div>
                    
//                     {field.type === 'select' && (
//                       <div className="col-span-2">
//                         <label className="block text-xs font-medium text-gray-700 mb-1">
//                           Options (comma separated)
//                         </label>
//                         <input
//                           type="text"
//                           className="w-full p-2 border rounded text-sm"
//                           value={field.options || ''}
//                           onChange={(e) => updateFormField(index, 'options', e.target.value)}
//                           placeholder="Option 1, Option 2, Option 3"
//                         />
//                       </div>
//                     )}
                    
//                     <div className="col-span-2">
//                       <label className="flex items-center">
//                         <input
//                           type="checkbox"
//                           className="mr-2"
//                           checked={field.required}
//                           onChange={(e) => updateFormField(index, 'required', e.target.checked)}
//                         />
//                         <span className="text-sm">Required Field</span>
//                       </label>
//                     </div>
//                   </div>
//                 </div>
//               ))}
              
//               <button
//                 type="button"
//                 onClick={addFormField}
//                 className="w-full py-2 mt-2 bg-blue-50 text-blue-600 rounded border border-blue-200 hover:bg-blue-100"
//               >
//                 + Add New Field
//               </button>
//             </div>
//           </div>
//         )}
//       </div>
      
//       {/* Layout Options */}
//       <div className="border-t border-gray-200 pt-4">
//         <button
//           type="button"
//           className="text-sm text-blue-600 hover:text-blue-800 mb-4"
//           onClick={() => setShowLayoutOptions(!showLayoutOptions)}
//         >
//           {showLayoutOptions ? 'Hide Layout Options' : 'Show Layout Options'}
//         </button>
        
//         {showLayoutOptions && (
//           <div className="space-y-4">
//             <h4 className="font-medium text-gray-800 mb-3">Layout Options</h4>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Layout Type
//               </label>
//               <select
//                 className="w-full p-2 border rounded"
//                 value={content?.layoutType || 'side-by-side'}
//                 onChange={(e) => updateContent('layoutType', e.target.value)}
//               >
//                 <option value="side-by-side">Side by Side (Info | Form)</option>
//                 <option value="form-top">Form Above Info</option>
//                 <option value="info-top">Info Above Form</option>
//                 <option value="form-only">Form Only</option>
//               </select>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Border Radius
//               </label>
//               <select
//                 className="w-full p-2 border rounded"
//                 value={content?.borderRadius || 'rounded-xl'}
//                 onChange={(e) => updateContent('borderRadius', e.target.value)}
//               >
//                 <option value="rounded-none">None</option>
//                 <option value="rounded">Small</option>
//                 <option value="rounded-md">Medium</option>
//                 <option value="rounded-lg">Large</option>
//                 <option value="rounded-xl">Extra Large</option>
//                 <option value="rounded-full">Full/Pill</option>
//               </select>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Shadow Style
//               </label>
//               <select
//                 className="w-full p-2 border rounded"
//                 value={content?.shadowStyle || 'shadow-xl'}
//                 onChange={(e) => updateContent('shadowStyle', e.target.value)}
//               >
//                 <option value="shadow-none">None</option>
//                 <option value="shadow-sm">Small</option>
//                 <option value="shadow">Medium</option>
//                 <option value="shadow-md">Large</option>
//                 <option value="shadow-lg">Extra Large</option>
//                 <option value="shadow-xl">2XL</option>
//               </select>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Padding
//               </label>
//               <select
//                 className="w-full p-2 border rounded"
//                 value={content?.padding || 'p-12'}
//                 onChange={(e) => updateContent('padding', e.target.value)}
//               >
//                 <option value="p-4">Small (16px)</option>
//                 <option value="p-6">Medium (24px)</option>
//                 <option value="p-8">Large (32px)</option>
//                 <option value="p-12">Extra Large (48px)</option>
//                 <option value="p-16">2XL (64px)</option>
//               </select>
//             </div>
            
//             <div className="mb-4">
//               <label className="flex items-center text-sm font-medium text-gray-700">
//                 <input
//                   type="checkbox"
//                   className="mr-2 h-4 w-4"
//                   checked={content?.showSocialIcons !== false}
//                   onChange={(e) => updateContent('showSocialIcons', e.target.checked)}
//                 />
//                 Show Social Media Icons
//               </label>
//             </div>
            
//             <div className="mb-4">
//               <label className="flex items-center text-sm font-medium text-gray-700">
//                 <input
//                   type="checkbox"
//                   className="mr-2 h-4 w-4"
//                   checked={content?.enableCaptcha === true}
//                   onChange={(e) => updateContent('enableCaptcha', e.target.checked)}
//                 />
//                 Enable CAPTCHA Protection
//               </label>
//               {content?.enableCaptcha && (
//                 <p className="text-xs text-gray-500 mt-1 ml-6">
//                   CAPTCHA will be enabled to protect against spam submissions.
//                 </p>
//               )}
//             </div>
//           </div>
//         )}
//       </div>
      
//       {/* Advanced Settings */}
//       <div className="border-t border-gray-200 pt-4">
//         <button
//           type="button"
//           className="text-sm text-blue-600 hover:text-blue-800 mb-4"
//           onClick={() => setShowAdvanced(!showAdvanced)}
//         >
//           {showAdvanced ? 'Hide Advanced Style Options' : 'Show Advanced Style Options'}
//         </button>
        
//         {showAdvanced && (
//           <div className="space-y-4">
//             <h4 className="font-medium text-gray-800 mb-3">Style Customization</h4>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Info Panel Background Color
//               </label>
//               <div className="flex items-center">
//                 <input
//                   type="color"
//                   className="h-10 w-10 p-0 border border-gray-300 rounded mr-2"
//                   value={content?.backgroundColor || '#1A4B84'}
//                   onChange={(e) => updateContent('backgroundColor', e.target.value)}
//                 />
//                 <input
//                   type="text"
//                   className="flex-1 p-2 border rounded"
//                   value={content?.backgroundColor || '#1A4B84'}
//                   onChange={(e) => updateContent('backgroundColor', e.target.value)}
//                   placeholder="#1A4B84"
//                 />
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Info Panel Text Color
//               </label>
//               <div className="flex items-center">
//                 <input
//                   type="color"
//                   className="h-10 w-10 p-0 border border-gray-300 rounded mr-2"
//                   value={content?.textColor || '#ffffff'}
//                   onChange={(e) => updateContent('textColor', e.target.value)}
//                 />
//                 <input
//                   type="text"
//                   className="flex-1 p-2 border rounded"
//                   value={content?.textColor || '#ffffff'}
//                   onChange={(e) => updateContent('textColor', e.target.value)}
//                   placeholder="#ffffff"
//                 />
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Form Background Color
//               </label>
//               <div className="flex items-center">
//                 <input
//                   type="color"
//                   className="h-10 w-10 p-0 border border-gray-300 rounded mr-2"
//                   value={content?.formBackgroundColor || '#ffffff'}
//                   onChange={(e) => updateContent('formBackgroundColor', e.target.value)}
//                 />
//                 <input
//                   type="text"
//                   className="flex-1 p-2 border rounded"
//                   value={content?.formBackgroundColor || '#ffffff'}
//                   onChange={(e) => updateContent('formBackgroundColor', e.target.value)}
//                   placeholder="#ffffff"
//                 />
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Button Background Color
//               </label>
//               <div className="flex items-center">
//                 <input
//                   type="color"
//                   className="h-10 w-10 p-0 border border-gray-300 rounded mr-2"
//                   value={content?.buttonColor || '#1A4B84'}
//                   onChange={(e) => updateContent('buttonColor', e.target.value)}
//                 />
//                 <input
//                   type="text"
//                   className="flex-1 p-2 border rounded"
//                   value={content?.buttonColor || '#1A4B84'}
//                   onChange={(e) => updateContent('buttonColor', e.target.value)}
//                   placeholder="#1A4B84"
//                 />
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Button Text Color
//               </label>
//               <div className="flex items-center">
//                 <input
//                   type="color"
//                   className="h-10 w-10 p-0 border border-gray-300 rounded mr-2"
//                   value={content?.buttonTextColor || '#ffffff'}
//                   onChange={(e) => updateContent('buttonTextColor', e.target.value)}
//                 />
//                 <input
//                   type="text"
//                   className="flex-1 p-2 border rounded"
//                   value={content?.buttonTextColor || '#ffffff'}
//                   onChange={(e) => updateContent('buttonTextColor', e.target.value)}
//                   placeholder="#ffffff"
//                 />
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Field Border Color
//               </label>
//               <div className="flex items-center">
//                 <input
//                   type="color"
//                   className="h-10 w-10 p-0 border border-gray-300 rounded mr-2"
//                   value={content?.fieldBorderColor || '#e5e7eb'}
//                   onChange={(e) => updateContent('fieldBorderColor', e.target.value)}
//                 />
//                 <input
//                   type="text"
//                   className="flex-1 p-2 border rounded"
//                   value={content?.fieldBorderColor || '#e5e7eb'}
//                   onChange={(e) => updateContent('fieldBorderColor', e.target.value)}
//                   placeholder="#e5e7eb"
//                 />
//               </div>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Form Field Border Radius
//               </label>
//               <select
//                 className="w-full p-2 border rounded"
//                 value={content?.fieldBorderRadius || 'rounded-lg'}
//                 onChange={(e) => updateContent('fieldBorderRadius', e.target.value)}
//               >
//                 <option value="rounded-none">None</option>
//                 <option value="rounded">Small</option>
//                 <option value="rounded-md">Medium</option>
//                 <option value="rounded-lg">Large</option>
//                 <option value="rounded-xl">Extra Large</option>
//               </select>
//             </div>
            
//             <div className="mb-4">
//               <label className="flex items-center text-sm font-medium text-gray-700">
//                 <input
//                   type="checkbox"
//                   className="mr-2 h-4 w-4"
//                   checked={content?.buttonFullWidth !== false}
//                   onChange={(e) => updateContent('buttonFullWidth', e.target.checked)}
//                 />
//                 Full Width Submit Button
//               </label>
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Custom CSS (Advanced)
//               </label>
//               <textarea
//                 className="w-full p-2 border rounded font-mono text-sm"
//                 value={content?.customCss || ''}
//                 onChange={(e) => updateContent('customCss', e.target.value)}
//                 placeholder=".contact-form { /* custom styles */ }"
//                 rows="4"
//               />
//               <p className="text-xs text-gray-500 mt-1">
//                 Add custom CSS to further customize your form (for advanced users).
//               </p>
//             </div>
//           </div>
//         )}
//       </div>
      
//       <div className="bg-blue-50 p-4 rounded-md mb-6">
//         <h4 className="text-blue-800 font-medium mb-2">Important Notes</h4>
//         <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
//           <li>Form validation will automatically handle required fields</li>
//           <li>All form submissions will be sent to your specified email</li>
//           <li>Social media links will only appear if URLs are provided</li>
//           {content?.enableCaptcha && (
//             <li>CAPTCHA requires additional server-side configuration</li>
//           )}
//         </ul>
//       </div>
//     </div>
//   );
// };

// export default ContactFormSectionForm;




